/**
 * Membership visibility filter (PODZ-1801).
 *
 * Hide retired memberships on the Profile → Membership tab. A membership is
 * hidden ONLY when its program is inactive AND the customer's membership is
 * expired. Every other combination remains visible — including grandfathered
 * plans (inactive program, still-active membership) and every membership on an
 * active program.
 *
 * Visibility matrix:
 *
 *   | membership_program_active | active (membership) | result | case                     |
 *   | ------------------------- | ------------------- | ------ | ------------------------ |
 *   | false                     | false (expired)     | HIDE   | retired                  |
 *   | false                     | true                | SHOW   | grandfathered (AC2)      |
 *   | true                      | true                | SHOW   | active program + active  |
 *   | true                      | false (expired)     | SHOW   | active program + expired |
 *   | undefined                 | (any)               | SHOW   | field absent → safe default |
 *
 * WIRE NOTE: `membership_program_active` is now emitted by the v2
 * `_membership.json.jbuilder` (`membership_program.is_active`, PODZ-1867) — an
 * inactive program arrives as `false`, so the hide rule is live. The strict
 * `=== false` check remains as a defensive safe default: any payload that omits
 * the flag (legacy responses, partial fixtures) treats the program as active
 * and shows the membership, never hiding on a merely-absent flag.
 */
import type { Membership } from '../types/customer';

/**
 * True when a membership should be shown on the Membership tab. The ONLY case
 * that hides is an inactive program with an expired membership. Strict
 * `=== false` on both arms — an absent (`undefined`) program flag never hides.
 */
export function isMembershipVisible(m: Membership): boolean {
  return !(m.membership_program_active === false && m.active === false);
}

/**
 * Returns only the visible memberships from a (possibly undefined) list.
 * Pure — no store/Vue coupling. Empty or undefined input → [].
 */
export function visibleMemberships(
  list: Membership[] | undefined,
): Membership[] {
  if (!list) return [];
  return list.filter(isMembershipVisible);
}
